import { useEffect, useState } from "react";
import { Reveal } from "@/components/landing/Reveal";

const STEPS = [
  {
    slug: "track",
    label: "Track",
    title: "Add an expense or shared payment.",
  },
  {
    slug: "understand",
    label: "Understand",
    title: "See the real-life trade-off and spending pattern.",
  },
  {
    slug: "decide",
    label: "Decide",
    title: "Make an intentional choice without guilt.",
  },
];

const STEP_SCREENS = [
  {
    src: "/screenshots/home.png",
    alt: "Splurge app home screen showing total spent this month, weekly and daily averages, and a monthly budget goal",
  },
  {
    src: "/screenshots/comparison.png",
    alt: "Splurge app Impact Calculator comparison tab with an AI financial insight and opportunity cost for the month",
  },
  {
    src: "/screenshots/wishlist.png",
    alt: "Splurge app wishlist tab showing the total wishlist amount and a button to add a goal",
  },
];

const PhoneStep = ({ step }) => (
  <img
    src={STEP_SCREENS[step].src}
    alt={STEP_SCREENS[step].alt}
    width="600"
    height="1299"
    className="aspect-[600/1299] w-full object-cover"
  />
);

export const HowItWorks = () => {
  const [step, setStep] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const timer = setInterval(() => setStep((s) => (s + 1) % STEPS.length), 3400);
    return () => clearInterval(timer);
  }, [paused]);

  return (
    <section id="how" className="scroll-mt-20 overflow-hidden bg-white py-24 lg:py-32">
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-2 lg:gap-10">
        <div>
          <Reveal>
            <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-splurge-primary">
              A healthier relationship with money
            </p>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="mt-4 text-4xl font-extrabold leading-[1.08] tracking-tight text-splurge-ink sm:text-5xl">
              Pause. See the trade-off.{" "}
              <span className="text-splurge-primary">Choose for yourself.</span>
            </h2>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-splurge-ink/60 md:text-lg">
              Splurge does not tell you to stop having fun. It gives you the context to decide
              what is worth it.
            </p>
          </Reveal>

          <Reveal delay={240}>
            <ol className="mt-10 space-y-3">
              {STEPS.map((s, i) => (
                <li key={s.slug}>
                  <button
                    type="button"
                    data-testid={`step-button-${s.slug}`}
                    aria-pressed={step === i}
                    onClick={() => {
                      setStep(i);
                      setPaused(true);
                    }}
                    className={`flex w-full items-center gap-5 rounded-2xl border p-5 text-left transition-all duration-300 ${
                      step === i
                        ? "border-splurge-primary/50 bg-splurge-lavender/70 shadow-lg shadow-splurge-primary/10"
                        : "border-splurge-lavender bg-white hover:border-splurge-violet/40 hover:bg-splurge-lavender/30"
                    }`}
                  >
                    <span
                      className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl text-base font-extrabold transition-colors duration-300 ${
                        step === i
                          ? "bg-splurge-primary text-white"
                          : "bg-splurge-lavender text-splurge-dark"
                      }`}
                    >
                      {i + 1}
                    </span>
                    <span>
                      <span className="block text-base font-extrabold tracking-tight text-splurge-ink">
                        {s.label}
                      </span>
                      <span className="mt-0.5 block text-sm leading-snug text-splurge-ink/60">
                        {s.title}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          </Reveal>
        </div>

        <Reveal delay={200} className="justify-self-center">
          <div className="relative w-[290px] sm:w-[330px]" data-testid="steps-phone">
            <div
              aria-hidden="true"
              className="absolute -inset-10 -z-10 rounded-full bg-splurge-violet/20 blur-3xl"
            />
            <div
              role="img"
              aria-label={`App preview for the step: ${STEPS[step].label}. ${STEPS[step].title}`}
              className="rounded-[3rem] bg-splurge-ink p-2.5 shadow-2xl shadow-splurge-dark/40"
            >
              <div className="overflow-hidden rounded-[2.45rem] bg-splurge-lavender">
                <div key={step} className="step-enter">
                  <PhoneStep step={step} />
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-center gap-2" aria-hidden="true">
              {STEPS.map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    step === i ? "w-6 bg-splurge-primary" : "w-1.5 bg-splurge-ink/20"
                  }`}
                />
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
